import { ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useWindowSize } from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import { MD_WIDTH } from '@Hotels-at-Home/hah-fe-core/dist/constants';
import HomeStack from '~/ui/pages/Home/common/HomeStack';
import CommonSwiper from '~/ui/pages/Home/common/CommonSwiper/CommonSwiper';
import { cart } from '~/constants/siteConfig';
import classnames from 'classnames';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

const BeddingSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$beddingSection' });
  const { t: tLink } = useTranslation('', {
    keyPrefix: '$local.$homePageLinks.$beddingSection',
  });
  const beddingRef = useRef(null);
  const sateenRef = useRef(null);
  const percaleRef = useRef(null);
  const [isSateen, setIsSateen] = useState(true);

  useGSAP(
    () => {
      // Set initial states
      gsap.set(sateenRef.current, { opacity: 1, display: 'block' });
      gsap.set(percaleRef.current, { opacity: 0, display: 'none' });
    },
    { scope: beddingRef, dependencies: [] },
  );

  const switchContent = (toSateen: boolean) => {
    const activeRef = toSateen ? sateenRef.current : percaleRef.current;
    const inactiveRef = toSateen ? percaleRef.current : sateenRef.current;

    gsap.context(() => {
      gsap.to(inactiveRef, {
        opacity: 0,
        duration: 0.15,
        onComplete: () => {
          gsap.set(inactiveRef, { display: 'none' });
          gsap.set(activeRef, { display: 'block' });
          gsap.to(activeRef, {
            opacity: 1,
            duration: 0.3,
          });
        },
      });
    }, beddingRef);

    setIsSateen(toSateen);
  };

  const { ymalName2 } = cart;

  const windowSize = useWindowSize();
  const isSmallScreen = Boolean(windowSize.width) && windowSize.width <= MD_WIDTH;

  return (
    <section className="hp-section bedding-section" id="hp-bedding">
      <div className="bedding-section-container">
        <CommonSwiper
          ymalName={ymalName2}
          isHomepageList
          ref={beddingRef}
          imgLoading="eager"
          spaceBetween={20}
          breakpoints={{
            960: {
              slidesPerView: 1,
            },
            768: {
              slidesPerView: 1,
            },
          }}
        />

        <div className="bedding-switcher">
          <button
            className={classnames({ active: isSateen })}
            onClick={() => switchContent(true)}
            type="button"
          >
            <span>{t('sateen')}</span>
            <p>{t('sateenDescription')}</p>
          </button>
          <button
            className={classnames({ active: !isSateen })}
            onClick={() => switchContent(false)}
            type="button"
          >
            <span>{t('percale')}</span>
            <p>{t('percaleDescription')}</p>
          </button>
        </div>
        <div className="bedding-switcher-content">
          <div
            ref={sateenRef}
            className={classnames('bedding-switcher-content-item sateen', { active: isSateen })}
          >
            <HomeStack
              title={t('hotelBeddingSets')}
              description={t('sateenBeddingDescription')}
              img="/images/homePage/beddingSection/pullman-sateen-bedding-sets.webp"
              imgMobile="/images/homePage/beddingSection/pullman-sateen-bedding-sets.webp"
              url={tLink('bedding')}
              loading="eager"
            />
            <HomeStack
              title={t('hotelLinen')}
              description={t('sateenLinenDescription')}
              img="/images/homePage/beddingSection/pullman-sateen-linen.webp"
              imgMobile="/images/homePage/beddingSection/pullman-sateen-linen.webp"
              url={tLink('bedding')}
              loading="eager"
              isFlipped
            />
          </div>
          <div
            ref={percaleRef}
            className={classnames('bedding-switcher-content-item percale', { active: !isSateen })}
          >
            <HomeStack
              title={t('hotelBeddingSets')}
              description={t('percaleBeddingDescription')}
              img="/images/homePage/beddingSection/pullman-percale-bedding-sets.webp"
              imgMobile="/images/homePage/beddingSection/pullman-percale-bedding-sets.webp"
              url={tLink('bedding')}
              loading="eager"
              isFlipped
            />
            <HomeStack
              title={t('hotelLinen')}
              description={t('percaleLinenDescription')}
              img="/images/homePage/beddingSection/pullman-percale-linen.webp"
              imgMobile="/images/homePage/beddingSection/pullman-percale-linen.webp"
              url={tLink('bedding')}
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BeddingSection;
